import React, { useEffect, useMemo } from 'react';
import { ComponentEditModal } from '../ComponentEditModal';
import { SaveToLibraryModal } from '../SaveToLibraryModal';
import { ConfirmDeleteModal } from '../ConfirmDeleteModal';
import { useComponentManagerState } from './useComponentManagerState';
import { useComponentManagerHandlers } from './useComponentManagerHandlers';
import { useSaveEditHandler } from './useSaveEditHandler';
import { LoadingState } from './LoadingState';
import { ErrorState } from './ErrorState';
import { EmptyState } from './EmptyState';
import { ComponentsList } from './ComponentsList';
import { INVISIBLE_COMPONENT_TYPES } from '../InvisibleComponentsBar';
import type { ComponentManagerProps } from './types';

export const ComponentManager: React.FC<ComponentManagerProps> = ({
  pageId,
  siteId,
  onAddComponent,
  onAddComponentAt,
  refreshTrigger,
  isAdmin = false,
  onComponentSaved,
  onComponentsChanged,
  layoutLinkHeaderComponents,
  layoutLinkFooterComponents,
  currentPageSlug,
}) => {
  const state = useComponentManagerState();

  const handlers = useComponentManagerHandlers({
    pageId,
    state,
    onComponentSaved,
    onComponentsChanged,
  });
  const { handleSaveEdit } = useSaveEditHandler({
    pageId,
    state,
    onComponentSaved,
    onComponentsChanged,
  });

  // Check if there are any visible (non-invisible) components
  // Pages may have invisible components like head-content but still be "empty" visually
  // NOTE: This must be before early returns to satisfy React hooks rules
  const hasVisibleComponents = useMemo(
    () => state.components.some(c => !INVISIBLE_COMPONENT_TYPES.includes(c.type)),
    [state.components]
  );

  // Reset state when pageId changes so loading shows for new pages
  useEffect(() => {
    state.setHasInitiallyLoaded(false);
    state.setIsLoading(true);
    state.setComponents([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageId]);

  useEffect(() => {
    handlers.loadComponents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageId, refreshTrigger]);

  if (state.isLoading) {
    return <LoadingState />;
  }

  if (state.error) {
    return <ErrorState error={state.error} onRetry={handlers.loadComponents} />;
  }

  return (
    <div className="min-h-screen">
      <div className="relative">
        {!hasVisibleComponents ? (
          <EmptyState isEditing={true} onAddComponent={onAddComponent} />
        ) : (
          <ComponentsList
            components={state.components}
            onAddComponentAt={onAddComponentAt}
            onEditComponent={handlers.handleEditComponent}
            onDeleteComponent={handlers.handleDeleteComponent}
            onMoveComponent={handlers.handleMoveComponent}
            onSaveToLibrary={handlers.handleSaveToLibrary}
            onDuplicateComponent={handlers.handleDuplicateComponent}
          />
        )}
      </div>

      <ComponentEditModal
        isOpen={state.isEditModalOpen}
        component={state.editingComponent}
        onClose={handlers.handleCloseEditModal}
        onSave={handleSaveEdit}
        isLoading={state.isSavingEdit}
        siteId={siteId}
        currentPageId={pageId}
        currentPageSlug={currentPageSlug}
        layoutLinkHeaderComponents={layoutLinkHeaderComponents}
        layoutLinkFooterComponents={layoutLinkFooterComponents}
        currentPageComponents={state.components}
      />

      <SaveToLibraryModal
        isOpen={state.isSaveToLibraryModalOpen}
        component={state.componentToSave}
        pageId={pageId}
        siteId={siteId}
        onClose={() => {
          state.setIsSaveToLibraryModalOpen(false);
          state.setComponentToSave(null);
        }}
        onSuccess={handlers.handleSaveToLibrarySuccess}
        isAdmin={isAdmin}
      />

      <ConfirmDeleteModal
        isOpen={state.isDeleteModalOpen}
        component={state.componentToDelete}
        onConfirm={handlers.handleConfirmDelete}
        onClose={handlers.handleCancelDelete}
        isLoading={state.isDeleting}
        error={state.deleteError}
      />
    </div>
  );
};

export default ComponentManager;
